/**
 * Canonical error codes. Every error response the API emits uses one of these
 * in `error.code`, across every phase of the platform.
 *
 * `NOT_FOUND` and `INTERNAL_ERROR` are additions to the list in
 * `ref/01-master-context.md`: the router needs a code for an unknown route, and
 * the top-level error handler needs one for an unexpected exception. Neither is
 * ever used for a competition-scoped resource — those must use the specific
 * `*_NOT_FOUND` codes so that IDOR probes cannot be distinguished from genuine
 * misses.
 */
export const ERROR_CODES = {
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  COMPETITION_NOT_FOUND: 'COMPETITION_NOT_FOUND',
  TEAM_NOT_FOUND: 'TEAM_NOT_FOUND',
  SUBMISSION_NOT_FOUND: 'SUBMISSION_NOT_FOUND',
  SUBMISSION_DEADLINE_PASSED: 'SUBMISSION_DEADLINE_PASSED',
  SUBMISSION_ALREADY_LOCKED: 'SUBMISSION_ALREADY_LOCKED',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  FILE_TYPE_NOT_ALLOWED: 'FILE_TYPE_NOT_ALLOWED',
  JUDGE_NOT_ASSIGNED: 'JUDGE_NOT_ASSIGNED',
  SCORE_ALREADY_LOCKED: 'SCORE_ALREADY_LOCKED',
  /** The round's scoring input mode (judge only / staff only / both) does not allow this action. */
  ROUND_SCORING_MODE_CONFLICT: 'ROUND_SCORING_MODE_CONFLICT',
  CONFLICT_OF_INTEREST: 'CONFLICT_OF_INTEREST',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',
  NOT_FOUND: 'NOT_FOUND',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  /**
   * Initial Super Admin Bootstrap (PR-024, API-039) rejected the attempt.
   * One code covers every precondition failure — wrong/missing credential,
   * unverified/inactive caller account, and "an active super admin already
   * exists" — deliberately: SEC-013 requires the endpoint not become an
   * oracle for platform state once bootstrap is no longer available.
   */
  BOOTSTRAP_UNAVAILABLE: 'BOOTSTRAP_UNAVAILABLE',
  /**
   * Platform Administration (Wave K, API-contract §2). Distinct from
   * `COMPETITION_NOT_FOUND`/`TEAM_NOT_FOUND` because the caller reaching
   * this code has already passed `requirePlatformPermission` — unlike those
   * two, there is no tenant-membership ambiguity to hide behind, so this
   * code is safe to return without an anti-enumeration concern of its own.
   */
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  /**
   * GOV-015/BR-064/API-036: the mutation would leave the platform with zero
   * active `super_admin` accounts (suspending or revoking the sole active
   * super admin). Rejected inside the same atomic statement that would have
   * performed the change — see `UsersRepository.suspend`/`revokeSuperAdmin`.
   */
  LAST_SUPER_ADMIN_PROTECTED: 'LAST_SUPER_ADMIN_PROTECTED',
  /** A Platform Administration mutation that requires an active, non-deleted target account found it was not. */
  USER_NOT_ACTIVE: 'USER_NOT_ACTIVE',
  /** Events shuttle booking conflict (full, duplicate direction, closed); `details.reason` carries the machine code. */
  EVENT_BOOKING_CONFLICT: 'EVENT_BOOKING_CONFLICT',
  /** A Competition Role mutation target competition/role/relation could not be resolved. */
  ROLE_NOT_FOUND: 'ROLE_NOT_FOUND',
  /**
   * BR-125/API-075: the mutation would leave a competition with zero
   * `competition_admin` grants (revoking its sole holder). Mirrors
   * `LAST_SUPER_ADMIN_PROTECTED` at the competition scope — enforced by both
   * `routes/competition-roles.ts` and the equivalent platform-level surface
   * in `routes/platform-roles.ts`.
   */
  LAST_COMPETITION_ADMIN_PROTECTED: 'LAST_COMPETITION_ADMIN_PROTECTED',
  /**
   * API-050/BR-082/SEC-019 (Wave R): the caller's session was minted from a
   * Temporary Initial Password and has not completed its forced first-login
   * password change yet. Every protected route except password-change,
   * logout, and minimal account/session verification returns this instead
   * of proceeding — enforced centrally in `middleware/authorize.ts`.
   */
  PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',
  /** A Judge Invitation token/id did not resolve to a usable (pending, unexpired) invitation. */
  JUDGE_INVITATION_NOT_FOUND: 'JUDGE_INVITATION_NOT_FOUND',
  /** Wave O, GOV-019/020: a Competition Creation Request id did not resolve, or the caller may not see it. */
  COMPETITION_REQUEST_NOT_FOUND: 'COMPETITION_REQUEST_NOT_FOUND',
  /** Wave O, DM-020: the request is not in a state the attempted transition (cancel/review/approve) allows. */
  COMPETITION_REQUEST_NOT_APPROVABLE: 'COMPETITION_REQUEST_NOT_APPROVABLE',
  /**
   * Wave O, GOV-019/020 §7 idempotency: this call lost the atomic approval
   * claim to a concurrent approve of the same request that has not finished
   * provisioning yet. A genuine retry after the winner completes gets the
   * idempotent `alreadyProvisioned: true` result instead — see
   * `CompetitionCreationRequestsRepository.claimForApproval` and the Wave O
   * delivery log's concurrency note.
   */
  COMPETITION_REQUEST_APPROVAL_IN_PROGRESS: 'COMPETITION_REQUEST_APPROVAL_IN_PROGRESS',
  /**
   * Wave T (API-060, AC-060): `:sessionId` in a self-service session-revoke
   * call did not resolve to one of the caller's own active sessions — same
   * response whether the id is unknown or belongs to a different account, so
   * the endpoint cannot be used to enumerate other users' session ids.
   */
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  /**
   * Wave T (API-061, GOV-029, PR-044, AC-058): the caller has no local
   * (`password`/`dev_password`) credential to change — an OIDC-only account
   * calling `POST /me/password` directly. The UI never shows the form to
   * begin with (UX-034); this is the non-leaking server-side backstop.
   */
  LOCAL_PASSWORD_NOT_AVAILABLE: 'LOCAL_PASSWORD_NOT_AVAILABLE',
  /** PR-127/API-067: a Submission Receipt id/submission did not resolve, or the caller may not see it. */
  SUBMISSION_RECEIPT_NOT_FOUND: 'SUBMISSION_RECEIPT_NOT_FOUND',
  /**
   * API-057/058, DATA-037: the target address of a `POST /me/email` request,
   * or the winner of a verify-time uniqueness race, is already the
   * authoritative email of another account. Reused for both cases — the
   * caller is already authenticated and deliberately choosing a new email,
   * so confirming "that address is taken" is not an anonymous-enumeration
   * oracle the way it would be for public registration.
   */
  EMAIL_ALREADY_IN_USE: 'EMAIL_ALREADY_IN_USE',
  /**
   * PR-197.., API-084..: an `:userId`/`:proofId` combination in the Member
   * Student Eligibility surface did not resolve to a proof the caller may
   * act on — unknown proof id, proof belongs to a different competition or
   * user, or the caller is probing another user's eligibility record.
   * Collapsed the same way `TEAM_NOT_FOUND` is, so an IDOR probe cannot
   * distinguish "doesn't exist" from "not yours".
   */
  ELIGIBILITY_NOT_FOUND: 'ELIGIBILITY_NOT_FOUND',
  ELIGIBILITY_PROOF_NOT_FOUND: 'ELIGIBILITY_PROOF_NOT_FOUND',
  /** API-ELIG-006 step 5: the proof id in a review decision is no longer the member's current/latest proof (superseded by a re-upload, or already decided). */
  ELIGIBILITY_PROOF_NOT_CURRENT: 'ELIGIBILITY_PROOF_NOT_CURRENT',
  /** PRIV-012/013, API-099 (v3.98): the caller already has a `pending` Account Erasure request — only one may be outstanding at a time. */
  ERASURE_REQUEST_ALREADY_PENDING: 'ERASURE_REQUEST_ALREADY_PENDING',
  /** API-099..101: an erasure request id did not resolve, or does not belong to the caller (self-service cancel). */
  ERASURE_REQUEST_NOT_FOUND: 'ERASURE_REQUEST_NOT_FOUND',
  /** API-100/101: approve/reject/cancel attempted on a request that is no longer `pending`. */
  ERASURE_REQUEST_NOT_PENDING: 'ERASURE_REQUEST_NOT_PENDING',
  /**
   * API-108/AC-278: `competition_categories.capacity` (non-null) has been
   * reached by that category's `active` team count — a new team cannot be
   * created in it, and an existing team cannot be moved into it, until a
   * slot frees up (another team is disbanded or moved out). Enforced inside
   * the same INSERT/UPDATE statement that creates or reassigns the team
   * (`TeamsRepository.createStatement`/`updateCategoryStatement`), not a
   * separate check-then-write, so two concurrent requests for the last slot
   * cannot both succeed.
   */
  CATEGORY_CAPACITY_FULL: 'CATEGORY_CAPACITY_FULL',
  /**
   * Registration Phase 2 (autosave, BR-156 follow-on): the caller's declared
   * `version` no longer matches `registration_form_answers.version` — the
   * row was updated (another tab, a concurrent autosave) since the caller
   * last read it. Optimistic-concurrency guard, not an authorization error.
   */
  REGISTRATION_VERSION_CONFLICT: 'REGISTRATION_VERSION_CONFLICT',
  /**
   * PR-253/BR-179/UX-263 (Student Journey Completion Wave): the request
   * reached the server after `competitions.registrationEndAt` had already
   * passed — the server's clock is authoritative, so a save/complete that
   * was legitimate when the page loaded is still rejected once it lands
   * late. Distinct from `REGISTRATION_VERSION_CONFLICT` (a concurrency
   * conflict, not a deadline) so the client can show deadline-specific
   * copy instead of a generic error.
   */
  REGISTRATION_WINDOW_CLOSED: 'REGISTRATION_WINDOW_CLOSED',
  /** Announcement email: this announcement was already emailed; a second send needs an explicit resend. */
  ANNOUNCEMENT_EMAIL_ALREADY_SENT: 'ANNOUNCEMENT_EMAIL_ALREADY_SENT',
  /** Announcement email: the recipient set changed since the count the organizer confirmed. */
  ANNOUNCEMENT_RECIPIENTS_CHANGED: 'ANNOUNCEMENT_RECIPIENTS_CHANGED',
  /**
   * v4.46 Competition Configuration JSON-only, Wave D: a Template id did not
   * resolve, or the caller may not see it - unknown id, `private` and
   * belongs to a different user, or has no revision yet. Same collapsed
   * shape as `COMPETITION_REQUEST_NOT_FOUND` so a private Template's
   * existence cannot be probed by id.
   */
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  /** Wave F: the imported configuration failed validation - malformed JSON, unsupported schema major, or a forbidden field. */
  CONFIG_IMPORT_INVALID: 'CONFIG_IMPORT_INVALID',
} as const

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]

export const ERROR_CODE_VALUES = Object.values(ERROR_CODES) as [ErrorCode, ...ErrorCode[]]

/**
 * HTTP status for each error code. Authorization failures deliberately collapse
 * onto 403/404 without distinguishing "exists but not yours" from "does not
 * exist", so cross-competition probing cannot enumerate resources.
 */
export const ERROR_CODE_HTTP_STATUS: Record<ErrorCode, number> = {
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  COMPETITION_NOT_FOUND: 404,
  TEAM_NOT_FOUND: 404,
  SUBMISSION_NOT_FOUND: 404,
  SUBMISSION_DEADLINE_PASSED: 409,
  SUBMISSION_ALREADY_LOCKED: 409,
  FILE_TOO_LARGE: 413,
  FILE_TYPE_NOT_ALLOWED: 415,
  JUDGE_NOT_ASSIGNED: 403,
  SCORE_ALREADY_LOCKED: 409,
  ROUND_SCORING_MODE_CONFLICT: 409,
  CONFLICT_OF_INTEREST: 403,
  VALIDATION_ERROR: 422,
  RATE_LIMITED: 429,
  NOT_FOUND: 404,
  INTERNAL_ERROR: 500,
  BOOTSTRAP_UNAVAILABLE: 403,
  USER_NOT_FOUND: 404,
  LAST_SUPER_ADMIN_PROTECTED: 409,
  USER_NOT_ACTIVE: 409,
  EVENT_BOOKING_CONFLICT: 409,
  ROLE_NOT_FOUND: 404,
  LAST_COMPETITION_ADMIN_PROTECTED: 409,
  PASSWORD_CHANGE_REQUIRED: 403,
  JUDGE_INVITATION_NOT_FOUND: 404,
  COMPETITION_REQUEST_NOT_FOUND: 404,
  COMPETITION_REQUEST_NOT_APPROVABLE: 409,
  COMPETITION_REQUEST_APPROVAL_IN_PROGRESS: 409,
  SESSION_NOT_FOUND: 404,
  LOCAL_PASSWORD_NOT_AVAILABLE: 409,
  SUBMISSION_RECEIPT_NOT_FOUND: 404,
  EMAIL_ALREADY_IN_USE: 409,
  ELIGIBILITY_NOT_FOUND: 404,
  ELIGIBILITY_PROOF_NOT_FOUND: 404,
  ELIGIBILITY_PROOF_NOT_CURRENT: 409,
  ERASURE_REQUEST_ALREADY_PENDING: 409,
  ERASURE_REQUEST_NOT_FOUND: 404,
  ERASURE_REQUEST_NOT_PENDING: 409,
  CATEGORY_CAPACITY_FULL: 409,
  REGISTRATION_VERSION_CONFLICT: 409,
  REGISTRATION_WINDOW_CLOSED: 409,
  ANNOUNCEMENT_EMAIL_ALREADY_SENT: 409,
  ANNOUNCEMENT_RECIPIENTS_CHANGED: 409,
  TEMPLATE_NOT_FOUND: 404,
  CONFIG_IMPORT_INVALID: 422,
}

/**
 * Client-safe default messages. Handlers may override with a more specific
 * message, but must never include stack traces, SQL, or internal identifiers.
 */
export const ERROR_CODE_DEFAULT_MESSAGE: Record<ErrorCode, string> = {
  UNAUTHENTICATED: 'Authentication is required.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  COMPETITION_NOT_FOUND: 'Competition not found.',
  TEAM_NOT_FOUND: 'Team not found.',
  SUBMISSION_NOT_FOUND: 'Submission not found.',
  SUBMISSION_DEADLINE_PASSED: 'The submission deadline has passed.',
  SUBMISSION_ALREADY_LOCKED: 'This submission is locked.',
  FILE_TOO_LARGE: 'The uploaded file is too large.',
  FILE_TYPE_NOT_ALLOWED: 'This file type is not allowed.',
  JUDGE_NOT_ASSIGNED: 'You are not assigned to this submission.',
  SCORE_ALREADY_LOCKED: 'This score is locked and cannot be modified.',
  ROUND_SCORING_MODE_CONFLICT: "This round's scoring input mode does not allow this action.",
  CONFLICT_OF_INTEREST: 'A declared conflict of interest prevents this action.',
  VALIDATION_ERROR: 'The request payload is invalid.',
  RATE_LIMITED: 'Too many requests. Please try again later.',
  NOT_FOUND: 'Not found.',
  INTERNAL_ERROR: 'An unexpected error occurred.',
  BOOTSTRAP_UNAVAILABLE: 'Initial platform bootstrap is not available.',
  USER_NOT_FOUND: 'User not found.',
  LAST_SUPER_ADMIN_PROTECTED:
    'This account is the platform’s only active super admin and cannot be removed or suspended.',
  USER_NOT_ACTIVE: 'This action requires an active account.',
  EVENT_BOOKING_CONFLICT: 'The shuttle reservation could not be completed.',
  ROLE_NOT_FOUND: 'Role grant not found.',
  LAST_COMPETITION_ADMIN_PROTECTED:
    'This competition must keep at least one competition administrator, so this grant cannot be revoked.',
  PASSWORD_CHANGE_REQUIRED: 'You must set a new password before continuing.',
  JUDGE_INVITATION_NOT_FOUND: 'This invitation is invalid, expired, or already used.',
  COMPETITION_REQUEST_NOT_FOUND: 'Competition creation request not found.',
  COMPETITION_REQUEST_NOT_APPROVABLE: 'This request is not in a state that allows that action.',
  COMPETITION_REQUEST_APPROVAL_IN_PROGRESS:
    'This request is already being approved. Please try again shortly.',
  SESSION_NOT_FOUND: 'Session not found.',
  LOCAL_PASSWORD_NOT_AVAILABLE:
    'This account is managed by an identity provider and has no local password to change.',
  SUBMISSION_RECEIPT_NOT_FOUND: 'Submission receipt not found.',
  EMAIL_ALREADY_IN_USE: 'This email address is already associated with an account.',
  ELIGIBILITY_NOT_FOUND: 'Eligibility record not found.',
  ELIGIBILITY_PROOF_NOT_FOUND: 'Enrollment proof not found.',
  ELIGIBILITY_PROOF_NOT_CURRENT: 'This is no longer the current enrollment proof.',
  ERASURE_REQUEST_ALREADY_PENDING: 'You already have a pending account erasure request.',
  ERASURE_REQUEST_NOT_FOUND: 'Erasure request not found.',
  ERASURE_REQUEST_NOT_PENDING: 'This erasure request has already been resolved.',
  CATEGORY_CAPACITY_FULL: 'This category has reached its team capacity.',
  REGISTRATION_VERSION_CONFLICT:
    'This registration was updated elsewhere. Reload to see the latest version before saving again.',
  REGISTRATION_WINDOW_CLOSED: 'The registration deadline has passed.',
  ANNOUNCEMENT_EMAIL_ALREADY_SENT: 'This announcement has already been emailed.',
  ANNOUNCEMENT_RECIPIENTS_CHANGED:
    'The recipients changed since you confirmed the count. Review the new count and try again.',
  TEMPLATE_NOT_FOUND: 'This template could not be found.',
  CONFIG_IMPORT_INVALID: 'This configuration file could not be imported.',
}
